import {
  FormBuilder,
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CarrierModel, ControlProperty } from '../../../../../../common';
import { LocalDateRangeModel } from '../../../../../models';
import { AllocationViewActionAttachmentsForm } from '../allocation-attachments.model';
import {
  AllocationViewAttachmentSealForm,
  AllocationViewSecurityWithAttachmentSealModel,
} from '../allocation-security';
import {
  AllocationViewActionQuestionForm,
  AllocationViewActionQuestionModel,
} from '../allocation-view-action-question.model';
import { AllocationViewActionRequestUliModel } from '../allocation-view-action-request.model';

const verificationValidator = (controlKey: 'dockVerified' | 'truckVerified'): ValidatorFn => {
  return (control: FormControl<boolean>): ValidationErrors | null => {
    if (control.parent && control.parent.dirty) {
      if (control.value && !control.parent.get(controlKey).value) {
        control.parent.get(controlKey).setErrors({ required: true });
        control.parent.get(controlKey).markAsDirty();
      }
    }

    return null;
  };
};

export class AllocationViewArrivalDockModel extends AllocationViewSecurityWithAttachmentSealModel {
  customGoods: boolean;
  dangerousGoods: boolean;
  dockSecurityQuestions: AllocationViewArrivalDockSecurityQuestions;
  dockVerified: boolean;
  freeTextCarrierName: string;
  truckVerified: boolean;
  swapBody: boolean;
}

export class AllocationViewArrivalDockSecurityQuestions {
  [key: string]: AllocationViewActionQuestionModel[];
}

export class AllocationViewArrivalDockForm {
  constructor(
    model: AllocationViewArrivalDockModel,
    fb: UntypedFormBuilder,
    public generalInformation: UntypedFormGroup = fb.group(
      new AllocationViewArrivalDockGeneralInformationForm(model, fb),
    ),
    public dockSecurityQuestions: UntypedFormGroup = fb.group(
      new AllocationViewArrivalDockSecurityQuestionsForm(model, fb),
    ),
    public attachments: UntypedFormGroup = fb.group(new AllocationViewArrivalDockAttachmentsForm(model, fb)),
  ) {}
}

export class AllocationViewArrivalDockGeneralInformationForm {
  constructor(
    model: AllocationViewArrivalDockModel,
    fb: FormBuilder,
    public carrier: ControlProperty<CarrierModel> = [model.carrier, []],
    public freeTextCarrierName: ControlProperty<string> = [model.freeTextCarrierName, []],
    public serviceTime: ControlProperty<LocalDateRangeModel> = [model.serviceTime, []],
    public firstName: ControlProperty<string> = [model.firstName, []],
    public lastName: ControlProperty<string> = [model.lastName, []],
    public customGoods: ControlProperty<boolean> = [model.customGoods, []],
    public truckVerified: ControlProperty<boolean> = [
      null,
      [Validators.requiredTrue, verificationValidator('dockVerified')],
    ],
    public dockVerified: ControlProperty<boolean> = [
      null,
      [Validators.requiredTrue, verificationValidator('truckVerified')],
    ],
    public propertyOfAnother: ControlProperty<boolean> = [model.propertyOfAnother, []],
    public dangerousGoods: ControlProperty<boolean> = [model.dangerousGoods, []],
    public truckSeal: UntypedFormGroup = fb.group(new AllocationViewAttachmentSealForm(model.truckSeal)),
    public remainingDrivingTime: ControlProperty<number> = [model.remainingDrivingTime, []],
    public trailerSeal: UntypedFormGroup = fb.group(new AllocationViewAttachmentSealForm(model.trailerSeal)),
    public hubId: ControlProperty<number> = [model.hubId, []],
    public truckId: ControlProperty<number> = [model.truckId, []],
    public ulis: ControlProperty<AllocationViewActionRequestUliModel[]> = [model.ulis, []],
    public swapBody: ControlProperty<boolean> = [model.swapBody, []],
  ) {}
}

export class AllocationViewArrivalDockSecurityQuestionsForm {
  constructor(model: AllocationViewArrivalDockModel, fb: UntypedFormBuilder) {
    const keys = Object.keys(model.dockSecurityQuestions).sort();

    keys.forEach((key: string, index: number) => {
      const isLast = keys.length - 1 === index;

      this[key] = fb.group(new AllocationViewArrivalDockSecurityQuestionForm(model, fb, key, isLast));
    });
  }
}

export class AllocationViewArrivalDockSecurityQuestionForm {
  public securityCheckPassed?: UntypedFormControl;

  constructor(
    model: AllocationViewArrivalDockModel,
    fb: UntypedFormBuilder,
    key: string,
    isLast: boolean,
    public questions: UntypedFormArray = fb.array([]),
  ) {
    model.dockSecurityQuestions[key].forEach((question: AllocationViewActionQuestionModel) => {
      questions.push(fb.group(new AllocationViewActionQuestionForm(question)));
    });

    if (isLast) {
      this.securityCheckPassed = fb.control(null, [Validators.required]);
    }
  }
}

export class AllocationViewArrivalDockAttachmentsForm {
  constructor(
    model: AllocationViewArrivalDockModel,
    fb: UntypedFormBuilder,
    public comments: UntypedFormGroup = fb.group(new AllocationViewActionAttachmentsForm(model.comments, fb)),
  ) {}
}
